import React, { createContext, useContext, useState, useEffect } from 'react';

export interface AppNotification {
  id: string;
  type: 'order' | 'need' | 'info' | 'success' | 'warning';
  title: string;
  message: string;
  time: Date;
  data?: any;
}

export const NotificationContext = createContext<{
  notifications: AppNotification[];
  addNotification: (notification: Omit<AppNotification, 'id' | 'time'>) => void;
  removeNotification: (id: string) => void;
  permission: NotificationPermission;
}>({
  notifications: [],
  addNotification: () => {},
  removeNotification: () => {},
  permission: 'default'
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setPermission(Notification.permission);
      }
    } catch (e) {
      console.warn("Notification permission check suppressed in iframe:", e);
    }
  }, []);

  const addNotification = (n: Omit<AppNotification, 'id' | 'time'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newN = { ...n, id, time: new Date() };
    setNotifications(prev => [newN, ...prev].slice(0, 5));

    try {
      if (permission === 'granted' && typeof window !== 'undefined' && 'Notification' in window) {
        new Notification(n.title, { body: n.message });
      }
    } catch (err) {
      console.warn("Browser notification suppressed:", err);
    }

    setTimeout(() => {
      removeNotification(id);
    }, 6000);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, removeNotification, permission }}>
      {children}
    </NotificationContext.Provider>
  );
};
